/**
 * Real `ProfileExtractionAPI` implementation backed by the HTTP client
 * (ADR-0040 Slice 1 Layer 3).
 *
 * Wire mapping (aligned to the server route at
 * `services/api/src/profile-extraction/route.ts` — the bare path is
 * the contract, not the legacy `/start` + `/latest` sub-paths):
 *
 *   - start:     POST /v1/sites/:site_id/profile-extraction
 *                202 + `{ profile_extraction_id, status: 'running' }`
 *                  (the ack — the actual extraction runs
 *                   fire-and-forget on the server's job pool;
 *                   the dashboard polls `getLatest` for the outcome)
 *                404 → null (feature flag PROFILE_EXTRACTION_ENABLED off,
 *                  OR ANTHROPIC_API_KEY unset on the deploy)
 *                412 → re-thrown verbatim (the dashboard's
 *                  `ApiErrorKind` taxonomy collapses 412 to
 *                  `'server_error'` via `statusToKind`; the bag does
 *                  not special-case it because the view layer's
 *                  `markFailed` path treats every non-404 / non-429
 *                  failure the same way — soft "couldn't analyze".
 *                  The server returns 412 when the site is not yet
 *                  `'active'` — DNS unverified — which is rare in
 *                  practice because the opt-in renders adjacent to
 *                  the DNS check).
 *                429 → re-thrown as ApiError('rate_limited') (the
 *                  http-client maps live 429s to AuthRateLimitError;
 *                  the bag is taxonomy-agnostic — it forwards
 *                  whatever the http client emitted).
 *
 *   - getLatest: GET /v1/sites/:site_id/profile-extraction
 *                200 + `{ extraction: <ProfileExtractionResult> | null }`
 *                  (the bag unwraps the envelope and returns the
 *                   bare result OR null — null when the merchant
 *                   has never started a run on this site, OR when
 *                   the latest row predates a future schema migration
 *                   the dashboard has not been taught about)
 *                404 → null (no SITE — handler returns 404 if the
 *                  site_id doesn't exist under the caller's tenancy;
 *                  the dashboard collapses that to the same
 *                  "no panel / no opt-in" state as feature-off)
 *
 * The 404 → null collapse is intentional. The dashboard treats
 * "feature unavailable", "no run yet", and "site not found under
 * tenancy" identically: the `VerifyStep` opt-in affordance is gated on
 * `start()` and `getLatest()` not returning null, so any 404 simply
 * means we don't render the opt-in (and the `SkillsStep` renders only
 * the default list). This mirrors the `getConnectorStatus` 404-collapse
 * pattern (real-sites.ts) and keeps the view layer branch count low.
 *
 * The bag DOES NOT mutate any production state. Applying a suggested
 * skill goes through `SkillAPI.saveForSite` — never this bag (ADR-0040
 * §4.4 — the no-auto-apply invariant on the dashboard side).
 */

import type {
  ProfileExtractionResult,
  SiteId,
} from '@sill/types';
import type { ProfileExtractionAPI } from '@sill/types/api';

import { ApiError, type HttpClient } from './http-client.js';

export interface RealProfileExtractionDeps {
  readonly http: HttpClient;
}

/** Wire envelope of `GET /v1/sites/:site_id/profile-extraction`.
 *  The server always returns `{ extraction: ... }` — the bag unwraps. */
interface GetExtractionEnvelope {
  extraction: ProfileExtractionResult | null;
}

/** Wire shape of the POST 202 ack. Structurally a
 *  `ProfileExtractionResult` minus the optional fields the server
 *  doesn't have yet when the row is freshly minted. */
interface StartExtractionAck {
  profile_extraction_id: string;
  status: 'running';
}

export function createRealProfileExtractionApi(
  deps: RealProfileExtractionDeps,
): ProfileExtractionAPI {
  return {
    async start(site_id: SiteId): Promise<ProfileExtractionResult | null> {
      try {
        const res = await deps.http.request<StartExtractionAck>({
          method: 'POST',
          path: `/v1/sites/${encodeURIComponent(site_id)}/profile-extraction`,
        });
        if (res === undefined) {
          throw new ApiError('bad_response', null);
        }
        // The 202 ack is structurally a `ProfileExtractionResult` with
        // only the two fields the freshly-minted row has. The dashboard
        // hook reads `status === 'running'` and transitions into the
        // poll loop; richer fields (suggested_skills / completed_at)
        // arrive via `getLatest`.
        return res;
      } catch (err) {
        // Feature-off (404) → null; the dashboard simply doesn't offer
        // the opt-in. Rate-limit (429) and precondition_failed (412)
        // and everything else re-thrown so the view layer can render a
        // typed toast.
        if (err instanceof ApiError && err.kind === 'not_found') {
          return null;
        }
        throw err;
      }
    },

    async getLatest(site_id: SiteId): Promise<ProfileExtractionResult | null> {
      try {
        const res = await deps.http.request<GetExtractionEnvelope>({
          method: 'GET',
          path: `/v1/sites/${encodeURIComponent(site_id)}/profile-extraction`,
        });
        if (res === undefined) {
          throw new ApiError('bad_response', null);
        }
        // Unwrap the `{ extraction }` envelope. Null means no run has
        // ever been started on this site under the caller's tenancy.
        return res.extraction;
      } catch (err) {
        // 404 means either the feature flag is off OR the site_id is
        // unknown under the caller's tenancy; both collapse to null.
        // The view layer treats both as "no panel + no opt-in".
        if (err instanceof ApiError && err.kind === 'not_found') {
          return null;
        }
        throw err;
      }
    },
  };
}
